import type { ShariahScreeningResponse } from "../api/services/shariah-screening.type";

/**
 * The Shariah screening fields that stock pages show to everyone when
 * SHARIAH_PUBLIC_FIELDS=summary. Anything not listed here stays behind
 * login: dollar amounts, revenue sources, history, ratings.
 *
 * This is the allowlist the server applies before data reaches the page (and
 * therefore the HTML and the route's .data response), so changing what is
 * "free" means changing this type and mapper only.
 */
export interface PublicShariahSummary {
  ticker: string;
  halalStatus: string;
  compliantRevenuePct: number;
  doubtfulRevenuePct: number;
  nonCompliantRevenuePct: number;
  debtRatioPct: number;
  liquidityRatioPct: number;
  /** Share of dividends to give away; equals the non-compliant revenue share. */
  purificationPct: number;
  lastUpdated: string | null;
}

const pct = (value: unknown) => {
  const n = Number(value);
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : 0;
};

export const toPublicShariahSummary = (
  screening: ShariahScreeningResponse,
): PublicShariahSummary => ({
  ticker: String(screening.ticker ?? "").toUpperCase(),
  halalStatus: String(screening.halal_status ?? ""),
  compliantRevenuePct: pct(screening.compliant_revenue_perc),
  doubtfulRevenuePct: pct(screening.doubtful_revenue_perc),
  nonCompliantRevenuePct: pct(screening.non_compliant_revenue_perc),
  debtRatioPct: pct(screening.debt_ratio),
  liquidityRatioPct: pct(screening.liquidity_ratio),
  purificationPct: pct(screening.non_compliant_revenue_perc),
  lastUpdated: screening.last_updated ?? screening.last_scanned ?? null,
});

/** Query key the page reads the public summary from (seeded by the loader). */
export const publicShariahKey = (ticker: string) =>
  ["shariah-public", ticker.toUpperCase()] as const;

/** A stock linked from another stock's page ("Other halal stocks in …"). */
export interface RelatedStock {
  symbol: string;
  companyName: string;
}

/** Query key the page reads related stocks from (seeded by the loader). */
export const relatedStocksKey = (ticker: string) =>
  ["related-stocks", ticker.toUpperCase()] as const;
